import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useJobListings, type JobListing } from '@/hooks/useJobListings';

const DEFAULT_PASSWORD = 'tmt2024';
const PASSWORD_STORAGE_KEY = 'tmt_admin_password';
const AUTO_LOCK_MINUTES = 5;

function getStoredPassword(): string {
  return localStorage.getItem(PASSWORD_STORAGE_KEY) || DEFAULT_PASSWORD;
}

type EmailSettings = {
  contact_email: string;
  support_email: string;
  info_email: string;
  noreply_email: string;
  newsletter_email: string;
  careers_email: string;
};

const defaultSettings: EmailSettings = {
  contact_email: '',
  support_email: '',
  info_email: '',
  noreply_email: '',
  newsletter_email: '',
  careers_email: '',
};

const iconOptions = [
  'ri-code-s-slash-line',
  'ri-scales-3-line',
  'ri-bank-card-line',
  'ri-palette-line',
  'ri-brain-line',
  'ri-customer-service-2-line',
  'ri-global-line',
  'ri-shield-check-line',
  'ri-bar-chart-box-line',
  'ri-team-line',
  'ri-megaphone-line',
];

const emptyJobForm = {
  title: '',
  department: '',
  location: '',
  type: 'Full-time',
  description: '',
  requirements: [''],
  icon: 'ri-team-line',
};

type FormSubmission = {
  id: number;
  name: string | null;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string | null;
  created_at: string;
};

type NewsletterSubscriber = {
  id: number;
  email: string;
  created_at: string;
};

type CareerApplication = {
  id: number;
  position: string;
  name: string;
  email: string;
  phone: string | null;
  linkedin: string | null;
  cover_letter: string | null;
  message: string | null;
  cv_filename: string | null;
  created_at: string;
};

type SignupApplication = {
  id: number;
  account_type: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  country_code: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  business_name: string | null;
  business_type: string | null;
  business_category: string | null;
  business_country: string | null;
  business_city: string | null;
  business_address: string | null;
  business_email: string | null;
  business_phone: string | null;
  business_country_code: string | null;
  business_landline: string | null;
  business_website: string | null;
  relevant_experience: string | null;
  motivation: string | null;
  kyc_filename: string | null;
  business_docs_filename: string | null;
  owner_passport_filename: string | null;
  reference_id: string | null;
  created_at: string;
};

function downloadCSV(headers: string[], rows: string[][], filename: string) {
  const escapeField = (field: string) => {
    if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  const bom = '\uFEFF';
  const csvContent = [
    headers.map(escapeField).join(','),
    ...rows.map((row) => row.map(escapeField).join(',')),
  ].join('\n');

  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === getStoredPassword()) {
      onUnlock();
    } else {
      setError(true);
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border-2 border-primary-600/40 rounded-full">
            <i className="ri-settings-3-line text-primary-400 text-xl" />
          </div>
          <h1 className="font-heading text-2xl text-foreground-50 mb-2">Admin Settings</h1>
          <p className="text-foreground-500 text-sm font-body">Enter the password to manage site settings.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              placeholder="Enter password"
              autoFocus
              className={`w-full px-4 py-3.5 bg-background-50 border rounded-xl text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none transition-colors pr-12 ${
                error ? 'border-red-400/50' : 'border-primary-700/30 focus:border-primary-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-foreground-300 transition-colors"
            >
              <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-xs font-body text-center">Incorrect password. Please try again.</p>
          )}

          <button
            type="submit"
            disabled={!password}
            className="w-full py-3.5 bg-primary-500 text-background-50 rounded-xl font-label text-sm font-semibold tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            Unlock Panel
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-foreground-500 text-xs font-body hover:text-foreground-300 transition-colors"
          >
            <i className="ri-arrow-left-line mr-1" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminSettingsPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'messages' | 'subscribers' | 'careers' | 'signups' | 'positions'>('settings');
  const [settings, setSettings] = useState<EmailSettings>({ ...defaultSettings });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  // Form submissions state
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionSearch, setSubmissionSearch] = useState('');

  // Newsletter subscribers state
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscriberSearch, setSubscriberSearch] = useState('');

  // Career applications state
  const [careers, setCareers] = useState<CareerApplication[]>([]);
  const [careersLoading, setCareersLoading] = useState(false);
  const [careerSearch, setCareerSearch] = useState('');

  // Signup applications state
  const [signups, setSignups] = useState<SignupApplication[]>([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupSearch, setSignupSearch] = useState('');

  // Job listings management
  const { jobs, departments, addJob, updateJob, deleteJob, resetToDefault } = useJobListings();
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [jobForm, setJobForm] = useState({ ...emptyJobForm });
  const [confirmDeleteJob, setConfirmDeleteJob] = useState<string | null>(null);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState<number | null>(null);
  const [confirmDeleteCareer, setConfirmDeleteCareer] = useState<number | null>(null);
  const [confirmDeleteSignup, setConfirmDeleteSignup] = useState<number | null>(null);

  // Auto-lock
  const lastActivityRef = useRef<number>(Date.now());
  const lockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!authenticated) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    const handler = () => recordActivity();
    events.forEach((e) => window.addEventListener(e, handler));

    lockTimerRef.current = setInterval(() => {
      const inactive = Date.now() - lastActivityRef.current;
      if (inactive > AUTO_LOCK_MINUTES * 60 * 1000) {
        handleLogout();
      }
    }, 30000); // Check every 30 seconds

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (lockTimerRef.current) clearInterval(lockTimerRef.current);
    };
  }, [authenticated, recordActivity]);

  useEffect(() => {
    if (!authenticated) return;
    loadSettings();
    loadSubmissions();
    loadSubscribers();
    loadCareers();
    loadSignups();
  }, [authenticated]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('site_settings').select('key, value');
      if (error) throw error;

      const merged = { ...defaultSettings };
      if (data) {
        data.forEach((row: { key: string; value: string }) => {
          if (row.key in merged) {
            merged[row.key as keyof EmailSettings] = row.value || '';
          }
        });
      }
      setSettings(merged);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_submissions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSubmissions(data || []);
    } catch (err) {
      console.error('Failed to load submissions:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSubscribers(data || []);
    } catch (err) {
      console.error('Failed to load subscribers:', err);
    } finally {
      setSubscribersLoading(false);
    }
  };

  const loadCareers = async () => {
    setCareersLoading(true);
    try {
      const { data, error } = await supabase
        .from('career_applications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCareers(data || []);
    } catch (err) {
      console.error('Failed to load career applications:', err);
    } finally {
      setCareersLoading(false);
    }
  };

  const loadSignups = async () => {
    setSignupsLoading(true);
    try {
      const { data, error } = await supabase
        .from('signup_applications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSignups(data || []);
    } catch (err) {
      console.error('Failed to load signup applications:', err);
    } finally {
      setSignupsLoading(false);
    }
  };

  // Job management handlers
  const openAddJob = () => {
    setJobForm({ ...emptyJobForm });
    setEditingJobId(null);
    setShowJobForm(true);
    setConfirmDeleteJob(null);
  };

  const openEditJob = (job: JobListing) => {
    setJobForm({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      description: job.description,
      requirements: [...job.requirements],
      icon: job.icon,
    });
    setEditingJobId(job.id);
    setShowJobForm(true);
    setConfirmDeleteJob(null);
  };

  const closeJobForm = () => {
    setShowJobForm(false);
    setEditingJobId(null);
    setJobForm({ ...emptyJobForm });
    setConfirmDeleteJob(null);
  };

  const handleJobSubmit = useCallback(() => {
    const trimmedReqs = jobForm.requirements.map((r) => r.trim()).filter((r) => r.length > 0);
    if (!jobForm.title.trim() || !jobForm.department.trim() || !jobForm.location.trim() || !jobForm.description.trim()) return;

    const payload = {
      title: jobForm.title.trim(),
      department: jobForm.department.trim(),
      location: jobForm.location.trim(),
      type: jobForm.type,
      description: jobForm.description.trim(),
      requirements: trimmedReqs.length > 0 ? trimmedReqs : ['No specific requirements listed.'],
      icon: jobForm.icon,
    };

    if (editingJobId) {
      updateJob(editingJobId, payload);
    } else {
      addJob(payload);
    }
    closeJobForm();
  }, [jobForm, editingJobId, addJob, updateJob]);

  const updateJobReq = (index: number, value: string) => {
    setJobForm((prev) => {
      const next = [...prev.requirements];
      next[index] = value;
      return { ...prev, requirements: next };
    });
  };

  const addJobReqField = () => {
    setJobForm((prev) => ({ ...prev, requirements: [...prev.requirements, ''] }));
  };

  const removeJobReqField = (index: number) => {
    setJobForm((prev) => {
      const next = prev.requirements.filter((_, i) => i !== index);
      return { ...prev, requirements: next.length > 0 ? next : [''] };
    });
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    setStatus(null);

    try {
      const entries = Object.entries(settings).map(([key, value]) => ({ key, value }));

      const { error } = await supabase.from('site_settings').upsert(entries, { onConflict: 'key' });

      if (error) throw error;

      setStatus({ type: 'success', message: 'Settings saved successfully! These will apply across your entire website.' });
    } catch (err: any) {
      setStatus({ type: 'error', message: err?.message || 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const handleDeleteMessage = async (id: number) => {
    try {
      const { error } = await supabase.from('form_submissions').delete().eq('id', id);
      if (error) throw error;
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Failed to delete message:', err);
    } finally {
      setConfirmDeleteMessage(null);
    }
  };

  const handleDeleteCareer = async (id: number) => {
    try {
      const { error } = await supabase.from('career_applications').delete().eq('id', id);
      if (error) throw error;
      setCareers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to delete career application:', err);
    } finally {
      setConfirmDeleteCareer(null);
    }
  };

  const handleDeleteSignup = async (id: number) => {
    try {
      const { error } = await supabase.from('signup_applications').delete().eq('id', id);
      if (error) throw error;
      setSignups((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Failed to delete signup:', err);
    } finally {
      setConfirmDeleteSignup(null);
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    if (lockTimerRef.current) clearInterval(lockTimerRef.current);
  };

  const openPasswordChange = () => {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwError('');
    setPwSuccess(false);
    setShowPasswordChange(true);
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');

    if (pwCurrent !== getStoredPassword()) {
      setPwError('Current password is incorrect.');
      return;
    }
    if (pwNew.length < 4) {
      setPwError('New password must be at least 4 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New passwords do not match.');
      return;
    }

    localStorage.setItem(PASSWORD_STORAGE_KEY, pwNew);
    setPwSuccess(true);
    setTimeout(() => {
      setShowPasswordChange(false);
      setPwSuccess(false);
    }, 1500);
  };

  const updateField = (key: keyof EmailSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setStatus(null);
  };

  const filteredSubmissions = submissions.filter(
    (s) =>
      (s.name || '').toLowerCase().includes(submissionSearch.toLowerCase()) ||
      s.email.toLowerCase().includes(submissionSearch.toLowerCase()) ||
      (s.subject || '').toLowerCase().includes(submissionSearch.toLowerCase())
  );

  const filteredSubscribers = subscribers.filter(
    (s) => s.email.toLowerCase().includes(subscriberSearch.toLowerCase())
  );

  const filteredCareers = careers.filter(
    (c) =>
      c.name.toLowerCase().includes(careerSearch.toLowerCase()) ||
      c.email.toLowerCase().includes(careerSearch.toLowerCase()) ||
      c.position.toLowerCase().includes(careerSearch.toLowerCase())
  );

  const filteredSignups = signups.filter(
    (s) =>
      s.email.toLowerCase().includes(signupSearch.toLowerCase()) ||
      (s.first_name || '').toLowerCase().includes(signupSearch.toLowerCase()) ||
      (s.last_name || '').toLowerCase().includes(signupSearch.toLowerCase()) ||
      (s.business_name || '').toLowerCase().includes(signupSearch.toLowerCase()) ||
      (s.reference_id || '').toLowerCase().includes(signupSearch.toLowerCase())
  );

  if (!authenticated) {
    return <PasswordGate onUnlock={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-background-50 pt-24 md:pt-32 pb-12 px-4 md:px-8 lg:px-12">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-2">
              / Admin
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-50">Site Settings</h1>
            <p className="text-foreground-400 text-sm mt-1 font-body">Manage emails, form submissions, and subscribers.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-foreground-500 text-xs font-body hidden sm:inline">
              Auto-locks after {AUTO_LOCK_MINUTES}min of inactivity
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-lock-unlock-line" />
              Lock
            </button>
            <button
              onClick={openPasswordChange}
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-key-line" />
              Password
            </button>
            <Link
              to="/"
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 transition-colors whitespace-nowrap"
            >
              View Site
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-primary-800/20 pb-1">
          {[
            { id: 'settings' as const, label: 'Email Settings', icon: 'ri-mail-settings-line', count: null },
            { id: 'messages' as const, label: 'Messages', icon: 'ri-message-3-line', count: submissions.length },
            { id: 'subscribers' as const, label: 'Subscribers', icon: 'ri-user-add-line', count: subscribers.length },
            { id: 'careers' as const, label: 'Careers', icon: 'ri-briefcase-line', count: careers.length },
            { id: 'signups' as const, label: 'Signups', icon: 'ri-user-star-line', count: signups.length },
            { id: 'positions' as const, label: 'Open Positions', icon: 'ri-briefcase-4-line', count: jobs.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 font-label text-xs tracking-wider rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.count !== null && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === tab.id ? 'bg-primary-500/15 text-primary-400' : 'bg-background-200 text-foreground-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="bg-background-100 border border-primary-800/20 rounded-2xl p-6 md:p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                      <i className="ri-mail-settings-line text-primary-400 text-lg" />
                    </div>
                    <div>
                      <h2 className="font-heading text-lg text-foreground-100">Business Emails</h2>
                      <p className="text-foreground-500 text-xs font-body">Configure the email addresses used across your website.</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        Contact Form Recipient
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        Where contact form submissions will be sent.
                      </p>
                      <div className="relative">
                        <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.contact_email}
                          onChange={(e) => updateField('contact_email', e.target.value)}
                          placeholder="contact@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        Support Email
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        Displayed on the Support and Contact pages.
                      </p>
                      <div className="relative">
                        <i className="ri-customer-service-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.support_email}
                          onChange={(e) => updateField('support_email', e.target.value)}
                          placeholder="support@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        General Info Email
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        General inquiries email shown on the website.
                      </p>
                      <div className="relative">
                        <i className="ri-information-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.info_email}
                          onChange={(e) => updateField('info_email', e.target.value)}
                          placeholder="info@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        No-Reply Email
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        Used for automated transactional emails.
                      </p>
                      <div className="relative">
                        <i className="ri-mail-send-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.noreply_email}
                          onChange={(e) => updateField('noreply_email', e.target.value)}
                          placeholder="noreply@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        Newsletter Subscriber Email
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        Where new newsletter subscriber notifications will be sent.
                      </p>
                      <div className="relative">
                        <i className="ri-mail-open-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.newsletter_email}
                          onChange={(e) => updateField('newsletter_email', e.target.value)}
                          placeholder="newsletter@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">
                        Careers Application Email
                      </label>
                      <p className="text-foreground-500 text-[11px] font-body mb-2">
                        Where new job application submissions will be sent.
                      </p>
                      <div className="relative">
                        <i className="ri-briefcase-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                        <input
                          type="email"
                          value={settings.careers_email}
                          onChange={(e) => updateField('careers_email', e.target.value)}
                          placeholder="careers@yourbusiness.com"
                          className="w-full pl-11 pr-4 py-3 bg-background-50 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {status && (
                  <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 ${
                    status.type === 'success'
                      ? 'bg-primary-500/10 border-primary-600/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <i className={`${
                      status.type === 'success' ? 'ri-checkbox-circle-line text-primary-400' : 'ri-error-warning-line text-red-400'
                    } text-lg mt-0.5`} />
                    <p className={`text-sm font-body ${
                      status.type === 'success' ? 'text-primary-200' : 'text-red-300'
                    }`}>
                      {status.message}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-foreground-500 text-xs font-body">
                    Settings are stored securely and persist across deployments.
                  </p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm font-semibold tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">Contact Form Submissions</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {submissions.length} total message{submissions.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                  <input
                    type="text"
                    value={submissionSearch}
                    onChange={(e) => setSubmissionSearch(e.target.value)}
                    placeholder="Search by name, email, subject..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => {
                    const data = submissionSearch ? filteredSubmissions : submissions;
                    const headers = ['ID', 'Name', 'Email', 'Phone', 'Subject', 'Message', 'Date'];
                    const rows = data.map((s) => [
                      String(s.id), s.name || '', s.email, s.phone || '', s.subject || '',
                      (s.message || '').replace(/\n/g, ' '), formatDate(s.created_at),
                    ]);
                    downloadCSV(headers, rows, `messages_${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={submissions.length === 0}
                  className="px-4 py-2.5 border border-primary-700/30 rounded-lg font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line" />
                  Export CSV
                </button>
              </div>
            </div>

            {submissionsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-inbox-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body">
                  {submissionSearch ? 'No messages match your search.' : 'No messages yet. They\'ll appear here when someone submits the contact form.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSubmissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-background-100 border border-primary-800/20 rounded-xl p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                          <i className="ri-user-line text-primary-400" />
                        </div>
                        <div>
                          <p className="font-heading text-sm text-foreground-100">
                            {sub.name || 'Anonymous'}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-foreground-500 text-xs font-body">
                            <span className="flex items-center gap-1">
                              <i className="ri-mail-line" /> {sub.email}
                            </span>
                            {sub.phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line" /> {sub.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-foreground-600 text-xs font-body">
                          {formatDate(sub.created_at)}
                        </span>
                        {confirmDeleteMessage === sub.id ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleDeleteMessage(sub.id)}
                              className="px-2.5 py-1 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md font-label text-[10px] hover:bg-red-500/20 transition-colors whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteMessage(null)}
                              className="px-2.5 py-1 border border-primary-700/30 rounded-md font-label text-[10px] text-foreground-400 hover:text-foreground-200 transition-colors whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteMessage(sub.id)}
                            className="w-7 h-7 flex items-center justify-center border border-primary-700/30 rounded-md text-foreground-500 hover:text-red-400 hover:border-red-400/40 transition-colors flex-shrink-0"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                    {sub.subject && (
                      <div className="mb-2">
                        <span className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-900 rounded-md text-[11px] font-label tracking-wider">
                          {sub.subject}
                        </span>
                      </div>
                    )}
                    {sub.message && (
                      <p className="text-foreground-300 text-sm font-body leading-relaxed bg-background-50 rounded-lg p-3">
                        {sub.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">Newsletter Subscribers</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {subscribers.length} total subscriber{subscribers.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                  <input
                    type="text"
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    placeholder="Search by email..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => {
                    const data = subscriberSearch ? filteredSubscribers : subscribers;
                    const headers = ['ID', 'Email', 'Subscribed On'];
                    const rows = data.map((s) => [
                      String(s.id), s.email, formatDate(s.created_at),
                    ]);
                    downloadCSV(headers, rows, `subscribers_${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={subscribers.length === 0}
                  className="px-4 py-2.5 border border-primary-700/30 rounded-lg font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line" />
                  Export CSV
                </button>
              </div>
            </div>

            {subscribersLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : filteredSubscribers.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-mail-open-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body">
                  {subscriberSearch ? 'No subscribers match your search.' : 'No subscribers yet. They\'ll appear here when someone subscribes via the footer form.'}
                </p>
              </div>
            ) : (
              <div className="bg-background-100 border border-primary-800/20 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-primary-800/20">
                        <th className="text-left px-5 py-3 font-label text-xs tracking-wider uppercase text-foreground-500">
                          Email
                        </th>
                        <th className="text-left px-5 py-3 font-label text-xs tracking-wider uppercase text-foreground-500">
                          Subscribed On
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscribers.map((sub) => (
                        <tr
                          key={sub.id}
                          className="border-b border-primary-800/10 last:border-b-0 hover:bg-background-50/50 transition-colors"
                        >
                          <td className="px-5 py-3.5 text-foreground-200 text-sm font-body">
                            <div className="flex items-center gap-2">
                              <i className="ri-mail-line text-foreground-500 text-xs" />
                              {sub.email}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-foreground-500 text-xs font-body">
                            {formatDate(sub.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Careers Tab */}
        {activeTab === 'careers' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">Career Applications</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {careers.length} total application{careers.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                  <input
                    type="text"
                    value={careerSearch}
                    onChange={(e) => setCareerSearch(e.target.value)}
                    placeholder="Search by name, email, position..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => {
                    const data = careerSearch ? filteredCareers : careers;
                    const headers = ['ID', 'Position', 'Name', 'Email', 'Phone', 'LinkedIn', 'Cover Letter', 'Message', 'CV Filename', 'Date'];
                    const rows = data.map((c) => [
                      String(c.id), c.position, c.name, c.email, c.phone || '', c.linkedin || '',
                      (c.cover_letter || '').replace(/\n/g, ' '), (c.message || '').replace(/\n/g, ' '),
                      c.cv_filename || '', formatDate(c.created_at),
                    ]);
                    downloadCSV(headers, rows, `career_applications_${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={careers.length === 0}
                  className="px-4 py-2.5 border border-primary-700/30 rounded-lg font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line" />
                  Export CSV
                </button>
              </div>
            </div>

            {careersLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : filteredCareers.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-briefcase-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body">
                  {careerSearch ? 'No applications match your search.' : 'No applications yet. They\'ll appear here when someone applies via the Careers page.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCareers.map((app) => (
                  <div
                    key={app.id}
                    className="bg-background-100 border border-primary-800/20 rounded-xl p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                          <i className="ri-user-line text-primary-400" />
                        </div>
                        <div>
                          <p className="font-heading text-sm text-foreground-100">
                            {app.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-foreground-500 text-xs font-body">
                            <span className="flex items-center gap-1">
                              <i className="ri-mail-line" /> {app.email}
                            </span>
                            {app.phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line" /> {app.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-foreground-600 text-xs font-body">
                          {formatDate(app.created_at)}
                        </span>
                        {confirmDeleteCareer === app.id ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleDeleteCareer(app.id)}
                              className="px-2.5 py-1 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md font-label text-[10px] hover:bg-red-500/20 transition-colors whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteCareer(null)}
                              className="px-2.5 py-1 border border-primary-700/30 rounded-md font-label text-[10px] text-foreground-400 hover:text-foreground-200 transition-colors whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteCareer(app.id)}
                            className="w-7 h-7 flex items-center justify-center border border-primary-700/30 rounded-md text-foreground-500 hover:text-red-400 hover:border-red-400/40 transition-colors flex-shrink-0"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-900 rounded-md text-[11px] font-label tracking-wider">
                        {app.position}
                      </span>
                    </div>
                    {app.cover_letter && (
                      <p className="text-foreground-300 text-sm font-body leading-relaxed bg-background-50 rounded-lg p-3 mb-2">
                        {app.cover_letter}
                      </p>
                    )}
                    {app.message && (
                      <p className="text-foreground-300 text-sm font-body leading-relaxed bg-background-50 rounded-lg p-3 mb-2">
                        {app.message}
                      </p>
                    )}
                    {app.linkedin && (
                      <div className="flex items-center gap-1.5 text-xs font-body">
                        <i className="ri-linkedin-box-line text-foreground-500" />
                        <a
                          href={app.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          View LinkedIn
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Signups Tab */}
        {activeTab === 'signups' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">Account Signups</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {signups.length} total signup{signups.length !== 1 ? 's' : ''}{' '}
                  ({signups.filter((s) => s.account_type === 'personal').length} personal,{' '}
                  {signups.filter((s) => s.account_type === 'business').length} business)
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                  <input
                    type="text"
                    value={signupSearch}
                    onChange={(e) => setSignupSearch(e.target.value)}
                    placeholder="Search by name, email, business..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => {
                    const data = signupSearch ? filteredSignups : signups;
                    const headers = [
                      'ID', 'Account Type', 'First Name', 'Last Name', 'Email', 'Country Code', 'Phone',
                      'Country', 'City', 'Address', 'Business Name', 'Business Type', 'Business Category',
                      'Business Country', 'Business City', 'Business Address', 'Business Email',
                      'Business Phone', 'Business Country Code', 'Business Landline', 'Business Website',
                      'Relevant Experience', 'Motivation', 'KYC Filename', 'Business Docs Filename',
                      'Owner Passport Filename', 'Reference ID', 'Date',
                    ];
                    const rows = data.map((s) => [
                      String(s.id), s.account_type, s.first_name || '', s.last_name || '', s.email,
                      s.country_code || '', s.phone || '', s.country || '', s.city || '', s.address || '',
                      s.business_name || '', s.business_type || '', s.business_category || '',
                      s.business_country || '', s.business_city || '', s.business_address || '',
                      s.business_email || '', s.business_phone || '', s.business_country_code || '',
                      s.business_landline || '', s.business_website || '',
                      (s.relevant_experience || '').replace(/\n/g, ' '),
                      (s.motivation || '').replace(/\n/g, ' '),
                      s.kyc_filename || '', s.business_docs_filename || '',
                      s.owner_passport_filename || '', s.reference_id || '', formatDate(s.created_at),
                    ]);
                    downloadCSV(headers, rows, `signups_${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={signups.length === 0}
                  className="px-4 py-2.5 border border-primary-700/30 rounded-lg font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line" />
                  Export CSV
                </button>
              </div>
            </div>

            {signupsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : filteredSignups.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-user-star-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body">
                  {signupSearch ? 'No signups match your search.' : 'No account signups yet. They\'ll appear here when someone creates an account.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSignups.map((s) => (
                  <div
                    key={s.id}
                    className="bg-background-100 border border-primary-800/20 rounded-xl p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full ${
                          s.account_type === 'business' ? 'bg-accent-100' : 'bg-primary-500/10'
                        }`}>
                          <i className={`${
                            s.account_type === 'business' ? 'ri-building-line text-accent-900' : 'ri-user-line text-primary-400'
                          }`} />
                        </div>
                        <div>
                          <p className="font-heading text-sm text-foreground-100">
                            {s.account_type === 'personal'
                              ? `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown'
                              : s.business_name || 'Unknown Business'}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-foreground-500 text-xs font-body">
                            <span className="flex items-center gap-1">
                              <i className="ri-mail-line" /> {s.email}
                            </span>
                            {s.phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line" /> {s.phone}
                              </span>
                            )}
                            {s.business_phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line" /> {s.business_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-label tracking-wider uppercase ${
                          s.account_type === 'business' ? 'bg-accent-100 text-accent-900' : 'bg-primary-500/10 text-primary-400'
                        }`}>
                          {s.account_type}
                        </span>
                        <span className="text-foreground-600 text-xs font-body">
                          {formatDate(s.created_at)}
                        </span>
                        {confirmDeleteSignup === s.id ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleDeleteSignup(s.id)}
                              className="px-2.5 py-1 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md font-label text-[10px] hover:bg-red-500/20 transition-colors whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteSignup(null)}
                              className="px-2.5 py-1 border border-primary-700/30 rounded-md font-label text-[10px] text-foreground-400 hover:text-foreground-200 transition-colors whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteSignup(s.id)}
                            className="w-7 h-7 flex items-center justify-center border border-primary-700/30 rounded-md text-foreground-500 hover:text-red-400 hover:border-red-400/40 transition-colors flex-shrink-0"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>

                    {s.account_type === 'personal' && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2 bg-background-50 rounded-lg p-3">
                        {s.country && (
                          <div>
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Country</span>
                            <p className="text-foreground-200 text-xs font-body">{s.country}</p>
                          </div>
                        )}
                        {s.city && (
                          <div>
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">City</span>
                            <p className="text-foreground-200 text-xs font-body">{s.city}</p>
                          </div>
                        )}
                        {s.address && (
                          <div className="col-span-2">
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Address</span>
                            <p className="text-foreground-200 text-xs font-body">{s.address}</p>
                          </div>
                        )}
                        {s.kyc_filename && (
                          <div className="col-span-2">
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">KYC Document</span>
                            <p className="text-foreground-200 text-xs font-body flex items-center gap-1">
                              <i className="ri-file-text-line text-foreground-500" /> {s.kyc_filename}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {s.account_type === 'business' && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-background-50 rounded-lg p-3">
                          {s.business_type && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Type</span>
                              <p className="text-foreground-200 text-xs font-body capitalize">{s.business_type.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {s.business_category && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Market Category</span>
                              <p className="text-foreground-200 text-xs font-body">{s.business_category}</p>
                            </div>
                          )}
                          {s.business_country && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Country</span>
                              <p className="text-foreground-200 text-xs font-body">{s.business_country}</p>
                            </div>
                          )}
                          {s.business_city && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">City</span>
                              <p className="text-foreground-200 text-xs font-body">{s.business_city}</p>
                            </div>
                          )}
                          {s.business_address && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Address</span>
                              <p className="text-foreground-200 text-xs font-body">{s.business_address}</p>
                            </div>
                          )}
                          {s.business_website && (
                            <div>
                              <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Website</span>
                              <a href={s.business_website} target="_blank" rel="noopener noreferrer" className="text-primary-400 text-xs font-body hover:text-primary-300 transition-colors">
                                {s.business_website}
                              </a>
                            </div>
                          )}
                        </div>
                        {s.motivation && (
                          <div className="bg-background-50 rounded-lg p-3">
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Motivation</span>
                            <p className="text-foreground-200 text-xs font-body leading-relaxed mt-1">{s.motivation}</p>
                          </div>
                        )}
                        {s.relevant_experience && (
                          <div className="bg-background-50 rounded-lg p-3">
                            <span className="text-foreground-500 text-[10px] font-label tracking-wider uppercase">Experience</span>
                            <p className="text-foreground-200 text-xs font-body leading-relaxed mt-1">{s.relevant_experience}</p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-3">
                          {s.business_docs_filename && (
                            <span className="inline-flex items-center gap-1 text-foreground-500 text-xs font-body">
                              <i className="ri-file-text-line" /> License: {s.business_docs_filename}
                            </span>
                          )}
                          {s.owner_passport_filename && (
                            <span className="inline-flex items-center gap-1 text-foreground-500 text-xs font-body">
                              <i className="ri-passport-line" /> Passport: {s.owner_passport_filename}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {s.reference_id && (
                      <div className="mt-3 pt-3 border-t border-primary-800/10 flex items-center gap-2">
                        <span className="text-foreground-600 text-[10px] font-label tracking-wider uppercase">Ref</span>
                        <span className="text-primary-400 text-xs font-body">{s.reference_id}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Positions Tab */}
        {activeTab === 'positions' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">Open Positions</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {jobs.length} position{jobs.length !== 1 ? 's' : ''} | {departments.length - 1} department{departments.length - 1 !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={openAddJob}
                className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-add-line" />
                Add Position
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Positions', value: jobs.length },
                { label: 'Departments', value: departments.length - 1 },
                { label: 'Full-time', value: jobs.filter((j) => j.type === 'Full-time').length },
                { label: 'Remote', value: jobs.filter((j) => j.location.toLowerCase().includes('remote')).length },
              ].map((stat) => (
                <div key={stat.label} className="p-4 bg-background-100 border border-primary-800/20 rounded-xl text-center">
                  <p className="font-heading text-2xl text-foreground-50">{stat.value}</p>
                  <p className="text-foreground-500 text-xs font-label tracking-wider mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Job List */}
            {jobs.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-briefcase-4-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body mb-4">No positions yet. Add your first one.</p>
                <button
                  onClick={openAddJob}
                  className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors"
                >
                  Add First Position
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="p-4 md:p-5 bg-background-100 border border-primary-800/20 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-primary-600/30 rounded-lg bg-background-50">
                        <i className={`${job.icon} text-primary-400 text-lg`} />
                      </div>
                      <div>
                        <h3 className="font-heading text-base text-foreground-100">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-foreground-500 text-xs font-label">
                          <span className="flex items-center gap-1"><i className="ri-building-line" /> {job.department}</span>
                          <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {job.location}</span>
                          <span className="px-2 py-0.5 bg-primary-500/10 text-primary-400 rounded-full text-[0.6rem]">{job.type}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openEditJob(job)}
                        className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-primary-400 hover:border-primary-500/40 transition-colors"
                        title="Edit"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      {confirmDeleteJob === job.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { deleteJob(job.id); setConfirmDeleteJob(null); }}
                            className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg font-label text-xs hover:bg-red-500/20 transition-colors"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmDeleteJob(null)}
                            className="px-3 py-1.5 border border-primary-700/30 rounded-lg font-label text-xs text-foreground-400 hover:text-foreground-200 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteJob(job.id)}
                          className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reset button */}
            {jobs.length > 0 && (
              <div className="mt-8 text-center">
                <button
                  onClick={resetToDefault}
                  className="text-foreground-500 text-xs font-body hover:text-foreground-300 transition-colors underline underline-offset-2"
                >
                  Reset all positions to default
                </button>
              </div>
            )}

            {/* Job Form Modal */}
            {showJobForm && (
              <div className="fixed inset-0 z-[60] bg-background-50/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto pt-20 pb-10 px-4">
                <div className="w-full max-w-2xl bg-background-50 border border-primary-800/20 rounded-2xl shadow-xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl text-foreground-50">
                      {editingJobId ? 'Edit Position' : 'Add New Position'}
                    </h2>
                    <button
                      onClick={closeJobForm}
                      className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                  </div>

                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Job Title</label>
                        <input
                          type="text"
                          value={jobForm.title}
                          onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="e.g. Senior Backend Engineer"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Department</label>
                        <input
                          type="text"
                          value={jobForm.department}
                          onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="e.g. Engineering"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Location</label>
                        <input
                          type="text"
                          value={jobForm.location}
                          onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="e.g. Beirut, Lebanon or Remote"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Type</label>
                        <select
                          value={jobForm.type}
                          onChange={(e) => setJobForm({ ...jobForm, type: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors"
                        >
                          <option>Full-time</option>
                          <option>Part-time</option>
                          <option>Contract</option>
                          <option>Internship</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Icon</label>
                      <div className="flex flex-wrap gap-2">
                        {iconOptions.map((icon) => (
                          <button
                            key={icon}
                            type="button"
                            onClick={() => setJobForm({ ...jobForm, icon })}
                            className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                              jobForm.icon === icon
                                ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                                : 'border-primary-700/30 text-foreground-500 hover:border-primary-500/40 hover:text-foreground-300'
                            }`}
                          >
                            <i className={`${icon} text-lg`} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Description</label>
                      <textarea
                        value={jobForm.description}
                        onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                        placeholder="Describe the role, responsibilities, and impact..."
                      />
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Requirements</label>
                      <div className="space-y-2">
                        {jobForm.requirements.map((req, i) => (
                          <div key={i} className="flex gap-2">
                            <input
                              type="text"
                              value={req}
                              onChange={(e) => updateJobReq(i, e.target.value)}
                              className="flex-1 px-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                              placeholder={`Requirement ${i + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeJobReqField(i)}
                              className="w-9 h-9 flex-shrink-0 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors"
                            >
                              <i className="ri-close-line" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addJobReqField}
                        className="mt-2 inline-flex items-center gap-1.5 text-primary-400 text-xs font-label tracking-wider hover:text-primary-300 transition-colors"
                      >
                        <i className="ri-add-line" /> Add Requirement
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-primary-800/20">
                    <button
                      onClick={closeJobForm}
                      className="px-5 py-2.5 border border-primary-700/30 rounded-full font-label text-sm tracking-wider text-foreground-300 hover:text-foreground-100 hover:border-primary-500/40 transition-colors whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleJobSubmit}
                      disabled={!jobForm.title.trim() || !jobForm.department.trim() || !jobForm.location.trim() || !jobForm.description.trim()}
                      className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {editingJobId ? 'Save Changes' : 'Add Position'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 z-[70] bg-background-50/90 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-background-50 border border-primary-800/20 rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-foreground-50">Change Password</h2>
              <button
                onClick={() => setShowPasswordChange(false)}
                className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {pwSuccess ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                  <i className="ri-check-line text-primary-400 text-2xl" />
                </div>
                <p className="text-foreground-100 font-body text-sm">Password changed successfully!</p>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Current Password</label>
                  <input
                    type="password"
                    value={pwCurrent}
                    onChange={(e) => { setPwCurrent(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">New Password</label>
                  <input
                    type="password"
                    value={pwNew}
                    onChange={(e) => { setPwNew(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="At least 4 characters"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => { setPwConfirm(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Re-enter new password"
                  />
                </div>

                {pwError && (
                  <p className="text-red-400 text-xs font-body">{pwError}</p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordChange(false)}
                    className="px-5 py-2.5 border border-primary-700/30 rounded-full font-label text-sm tracking-wider text-foreground-300 hover:text-foreground-100 hover:border-primary-500/40 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!pwCurrent || !pwNew || !pwConfirm}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    Update Password
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}